/**
 * When the next room streams reconciliation runs (YOUM-244):
 * - healthy: every HEALTHY_MS, spread by ±JITTER so that clients which
 *   (re)joined together don't list together;
 * - recovery: every RECOVERY_MS (same spread), entered on a sign that the
 *   room streams may be out of sync: a join or rejoin, a pass that changed the
 *   store or whose list failed, an exhausted subscribe;
 * - recovery ends after RECOVERED_PASSES consecutive clean passes, or after
 *   MAX_RECOVERY_PASSES passes whatever their outcome; signs are then ignored
 *   for COOLDOWN_MS, so a permanent failure can't keep a client listing every
 *   RECOVERY_MS;
 * - a recovery starting less than COOLDOWN_MS after the previous one ended
 *   resumes its pass count: a failure that comes back after clean passes
 *   still exhausts the recovery;
 * - a pass is not clean when a sign was received since the previous pass
 *   ended: the store can't show a subscribe that failed again;
 * - an immediate pass runs at least MIN_GAP_MS after the previous one.
 */
export class RoomStreamsReconcileCadence {
  static readonly HEALTHY_MS = 30_000;
  static readonly RECOVERY_MS = 5_000;
  static readonly MIN_GAP_MS = 5_000;
  static readonly JITTER = 0.2;
  static readonly RECOVERED_PASSES = 2;
  static readonly MAX_RECOVERY_PASSES = 12;
  static readonly COOLDOWN_MS = 5 * 60_000;

  // Passes since the recovery started, null when healthy.
  private _recoveryPasses: number | null = null;
  private _cleanPasses = 0;
  // When the previous recovery ended, and its pass count then.
  private _recoveryEndedAt = -Infinity;
  private _endedRecoveryPasses = 0;
  // A sign received since the previous pass ended: the next one to end, in
  // flight or not, is not clean.
  private _outOfSyncSincePass = false;
  private _cooldownUntil = -Infinity;
  private _lastPassAt = -Infinity;
  private _immediate = false;

  constructor(
    private readonly _now: () => number = () => Date.now(),
    private readonly _random: () => number = Math.random
  ) {}

  get isRecovering(): boolean {
    return this._recoveryPasses !== null;
  }

  // A sign that the room streams are out of sync. False when ignored (cooling
  // down): nothing changed, the next pass stays when it was.
  outOfSync(immediate = false): boolean {
    if (!this.recover()) {
      return false;
    }

    this._outOfSyncSincePass = true;
    if (immediate) {
      this._immediate = true;
    }
    return true;
  }

  // Recovery, unless cooling down (false then): after a join or rejoin, whose
  // streams may be republished after the join list, or a pass not clean.
  recover(): boolean {
    if (this._now() < this._cooldownUntil) {
      return false;
    }

    if (!this.isRecovering) {
      this._recoveryPasses =
        this._now() - this._recoveryEndedAt <
        RoomStreamsReconcileCadence.COOLDOWN_MS
          ? this._endedRecoveryPasses
          : 0;
      this._cleanPasses = 0;
    }
    return true;
  }

  // A pass as soon as the minimum gap allows, recovery unchanged.
  requestPass(): void {
    this._immediate = true;
  }

  nextDelayMs(): number {
    if (this._immediate) {
      return Math.max(
        0,
        this._lastPassAt + RoomStreamsReconcileCadence.MIN_GAP_MS - this._now()
      );
    }

    const base = this.isRecovering
      ? RoomStreamsReconcileCadence.RECOVERY_MS
      : RoomStreamsReconcileCadence.HEALTHY_MS;

    return Math.round(
      base * (1 + RoomStreamsReconcileCadence.JITTER * (2 * this._random() - 1))
    );
  }

  passStarted(): void {
    this._lastPassAt = this._now();
    this._immediate = false;
  }

  // `clean`: the list succeeded and the store already matched it.
  passDone(clean: boolean): void {
    const isClean = clean && !this._outOfSyncSincePass;
    this._outOfSyncSincePass = false;

    if (this._recoveryPasses === null) {
      if (!isClean) {
        this.recover();
      }
      return;
    }

    this._recoveryPasses++;
    this._cleanPasses = isClean ? this._cleanPasses + 1 : 0;

    if (this._cleanPasses >= RoomStreamsReconcileCadence.RECOVERED_PASSES) {
      this.endRecovery();
    } else if (
      this._recoveryPasses >= RoomStreamsReconcileCadence.MAX_RECOVERY_PASSES
    ) {
      this.endRecovery();
      this._cooldownUntil =
        this._now() + RoomStreamsReconcileCadence.COOLDOWN_MS;
    }
  }

  private endRecovery(): void {
    this._recoveryEndedAt = this._now();
    this._endedRecoveryPasses = this._recoveryPasses ?? 0;
    this._recoveryPasses = null;
  }
}
